import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Instagram, MessageCircle, Loader2, Clock, CalendarX2, Sparkle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ADDON_LABELS } from "@/components/BuildConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Wedding", "Birthday", "Corporate event", "Other"];
const PACKAGE_OPTIONS = [
  { id: "essentials", label: "The Essentials — from $500" },
  { id: "full_pour", label: "The Full Pour — from $800" },
  { id: "zero_proof", label: "The Zero Proof — from $400" },
];

const EMPTY = { name: "", email: "", phone: "", event_date: "", event_type: "", guests: "", package: "", message: "" };

export default function BookingForm({ build }) {
  const [mode, setMode] = useState("enquiry");
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setForm((f) => ({ ...f, package: build.pkg }));
  }, [build.pkg]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const today = new Date().toISOString().split("T")[0];
  const deposit = form.package === "essentials" ? 200 : form.package === "full_pour" ? 320 : form.package === "zero_proof" ? 160 : null;
  const guestCount = parseInt(form.guests, 10) || 0;
  const totalGuests = guestCount + (build.extraGuests || 0) * 25;
  const barLimit = form.package === "full_pour" ? 100 : 75;
  const needsSecond = !!form.package && totalGuests >= barLimit;
  const allAddons = [...build.addons, ...(needsSecond ? ["second_bartender_required"] : [])];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, {
        ...form,
        guests: guestCount,
        addons: allAddons,
        extra_hours: build.hours || 0,
        extra_guests: (build.extraGuests || 0) * 25,
        intent: mode,
      });
      sessionStorage.setItem(
        "ss_booking",
        JSON.stringify({ reference: data.reference, date: form.event_date, venue: form.event_type, guests: form.guests, package: form.package })
      );

      toast.success(mode === "booking" ? "Booking received!" : "Enquiry sent!");
      navigate(`/confirmation?ref=${data.reference}&type=${mode}&pkg=${form.package || ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="bg-blush px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <div className="overflow-hidden rounded-[2rem] bg-espresso-deep shadow-[0_40px_80px_-40px_rgba(29,19,12,0.6)]">
            <div className="grid lg:grid-cols-5">
              <div className="grain relative bg-gradient-to-b from-espresso to-espresso-deep p-8 sm:p-12 lg:col-span-2 lg:p-14">
                <span className="eyebrow text-gold">
                  <Sparkle className="h-3.5 w-3.5" /> {mode === "booking" ? "Instant booking" : "Get a quote"}
                </span>
                <h2 className="mt-6 font-display text-4xl leading-tight text-cream sm:text-5xl">
                  Let's shake up your <em className="italic text-rose">event</em>
                </h2>
                <p className="mt-5 text-sm leading-relaxed text-cream/65 sm:text-base">
                  {mode === "booking"
                    ? "Lock in your date — a 40% deposit, paid securely after you submit, reserves it. We'll confirm within 24 hours."
                    : "Tell us the essentials and we'll send a tailored quote within 24 hours."}
                </p>

                <div className="mt-10 space-y-4" data-testid="booking-policies">
                  <p className="flex items-start gap-4 text-sm leading-relaxed text-cream/80" data-testid="policy-4hr">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream/10 text-gold">
                      <Clock className="h-4 w-4" />
                    </span>
                    <span><strong className="text-cream">5-hour minimum.</strong> Every party runs at least five hours — that includes setup and breakdown.</span>
                  </p>
                  <p className="flex items-start gap-4 text-sm leading-relaxed text-cream/80" data-testid="policy-cancellation">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream/10 text-gold">
                      <CalendarX2 className="h-4 w-4" />
                    </span>
                    <span>
                      <strong className="text-cream">40% deposit, 14-day protection.</strong> Cancel 14+ days before your
                      event for a full deposit refund.
                    </span>
                  </p>
                  <p className="flex items-start gap-4 text-sm leading-relaxed text-cream/80" data-testid="policy-tabc">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream/10 text-gold">
                      <Sparkle className="h-4 w-4" />
                    </span>
                    <span>
                      <strong className="text-cream">TABC certified & compliant.</strong> Every bartender on our staff is
                      TABC certified and compliant.
                    </span>
                  </p>
                </div>

                <div className="mt-10 space-y-3 border-t border-cream/10 pt-8 text-sm text-cream/60">
                  <p className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-gold" /> La Cantera — Boerne area
                  </p>
                  <p className="text-[11px] tracking-wide text-cream/40" data-testid="area-upcharge-note">
                    Anything outside a 30-mile radius is subject to an upcharge.
                  </p>
                  <p className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-gold" />
                    <a href="tel:+12109204677" data-testid="booking-phone-link" className="transition-colors hover:text-gold">
                      (210) 920-4677
                    </a>
                  </p>
                  <p className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-gold" />
                    <a
                      href="mailto:Jasmine@shakensociety.com"
                      data-testid="booking-email-link"
                      className="transition-colors hover:text-gold"
                    >
                      Jasmine@shakensociety.com
                    </a>
                  </p>
                  <p className="flex items-center gap-3">
                    <Instagram className="h-4 w-4 text-gold" />
                    <a
                      href="https://www.instagram.com/TheShakenSociety"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="booking-instagram-link"
                      className="transition-colors hover:text-gold"
                    >
                      @TheShakenSociety
                    </a>
                  </p>
                  <p className="flex items-center gap-3">
                    <MessageCircle className="h-4 w-4 text-gold" />
                    <a
                      href="sms:+12109204677?&body=Hi%20Shaken%20Society!%20I%27d%20like%20to%20know%20more%20about%20your%20mobile%20bar%20service."
                      data-testid="booking-sms-link"
                      className="transition-colors hover:text-gold"
                    >
                      Prefer to text? (210) 920-4677
                    </a>
                  </p>
                </div>
              </div>

              <div className="p-8 sm:p-12 lg:col-span-3 lg:p-14">
                <>
                    <div className="mb-6 flex gap-2 rounded-full border border-cream/15 p-1.5">
                      {[
                        { id: "enquiry", label: "Get a quote" },
                        { id: "booking", label: "Instant booking" },
                      ].map((m) => (
                        <button
                          key={m.id}
                          data-testid={`booking-mode-${m.id}`}
                          onClick={() => setMode(m.id)}
                          className={`flex-1 rounded-full px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.16em] transition-all duration-300 ${
                            mode === m.id ? "bg-gold text-espresso" : "text-cream/60 hover:text-cream"
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>

                    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
                      <input
                        data-testid="booking-name-input"
                        className="field"
                        placeholder="Your name *"
                        value={form.name}
                        onChange={set("name")}
                        required
                        minLength={2}
                      />
                      <input
                        data-testid="booking-phone-input"
                        className="field"
                        type="tel"
                        placeholder="Phone number"
                        value={form.phone}
                        onChange={set("phone")}
                      />
                      <input
                        data-testid="booking-customer-email-input"
                        className="field"
                        type="email"
                        placeholder="Email (optional — for replies)"
                        value={form.email}
                        onChange={set("email")}
                      />
                      <input
                        data-testid="booking-date-input"
                        className="field"
                        type="date"
                        min={today}
                        value={form.event_date}
                        onChange={set("event_date")}
                        required
                      />
                      {mode === "booking" && (
                        <select
                          data-testid="booking-event-type-select"
                          className="field appearance-none bg-white"
                          value={form.event_type}
                          onChange={set("event_type")}
                        >
                          <option value="">
                            Event type
                          </option>
                          {EVENT_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      )}
                      <input
                        data-testid="booking-guests-input"
                        className="field"
                        type="number"
                        min="1"
                        placeholder="Guest count *"
                        value={form.guests}
                        onChange={set("guests")}
                        required
                      />
                      {mode === "booking" && (
                        <select
                          data-testid="booking-package-select"
                          className="field appearance-none bg-white"
                          value={form.package}
                          onChange={set("package")}
                          required
                        >
                          <option value="" disabled>
                            Package *
                          </option>
                          {PACKAGE_OPTIONS.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                      {(build.addons.length > 0 || build.hours > 0 || build.extraGuests > 0 || needsSecond) && (
                        <div className="sm:col-span-2" data-testid="booking-selected-addons">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-cream/50">
                            Selected add-ons
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {build.addons.map((id) => (
                              <span
                                key={id}
                                data-testid={`booking-addon-chip-${id}`}
                                className="rounded-full border border-gold/50 bg-gold/10 px-3.5 py-1.5 text-xs text-cream"
                              >
                                {ADDON_LABELS[id]}
                              </span>
                            ))}
                            {build.extraGuests > 0 && (
                              <span
                                data-testid="booking-addon-chip-extra_guests"
                                className="rounded-full border border-gold/50 bg-gold/10 px-3.5 py-1.5 text-xs text-cream"
                              >
                                Additional guests: +{build.extraGuests * 25} (+${build.extraGuests * 100})
                              </span>
                            )}
                            {build.hours > 0 && (
                              <span
                                data-testid="booking-addon-chip-extra_hours"
                                className="rounded-full border border-gold/50 bg-gold/10 px-3.5 py-1.5 text-xs text-cream"
                              >
                                Extra time: {build.hours} {build.hours === 1 ? "hour" : "hours"} (+${build.hours * 125})
                              </span>
                            )}
                            {needsSecond && (
                              <span
                                data-testid="booking-addon-chip-second_bartender"
                                className="rounded-full border border-gold/50 bg-gold/10 px-3.5 py-1.5 text-xs text-cream"
                              >
                                2nd bartender — required
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      <textarea
                        data-testid="booking-message-input"
                        className="field min-h-[110px] resize-none sm:col-span-2"
                        placeholder="Extra information — venue, theme, favorite spirits, anything else we should know?"
                        value={form.message}
                        onChange={set("message")}
                      />

                      <button
                        type="submit"
                        data-testid="booking-submit-button"
                        disabled={sending}
                        className="flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-espresso transition-all duration-300 hover:scale-[1.02] hover:bg-cream disabled:opacity-60 sm:col-span-2"
                      >
                        {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                        {sending ? "Sending..." : "Book your date"}
                      </button>
                      <p className="text-center text-[11px] tracking-wide text-cream/40 sm:col-span-2" data-testid="booking-fine-print">
                        {deposit
                          ? `Instant booking pays a $${deposit} deposit (40% of package). `
                          : ""}
                        5-hour minimum (including setup & breakdown) · deposit fully refundable 14+ days before your event
                      </p>
                    </form>
                  </>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}